import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { CheckCircle, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GROUP_TYPES = [
  'TRA / Tenants & Residents Association',
  "Residents' Association",
  'Leaseholder Association',
  'Resident panel / scrutiny group',
  'Community group',
  'Other resident-led structure',
];

export const GroupInterestForm = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', organisation: '', role: '', group_type: '',
    engagement_answer: '', early_testing: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/waiting-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          user_type: 'resident_association',
          interest_type: 'resident_group',
          organisation: formData.organisation,
          role: formData.role || null,
          group_type: formData.group_type || null,
          desired_features: formData.engagement_answer || null,
          interested_in_pilot: formData.early_testing,
          source_page: '/resident-groups',
        }),
      });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
    } catch {
      toast.error("Something went wrong — please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl p-8 text-center shadow-lg border border-[#B5541B]/10" data-testid="group-form-success">
        <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-7 h-7 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-[#1F1E1E] mb-2">Thanks — we'll be in touch</h3>
        <p className="text-sm text-[#3B3535]/70 leading-relaxed">
          We read every response from resident leaders. We'll reach out to arrange a conversation about your group.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-[#B5541B]/10 space-y-4 text-left" data-testid="group-interest-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Input
          required
          placeholder="Your name *"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          data-testid="group-form-name"
          className="h-11"
        />
        <Input
          required
          type="email"
          placeholder="Email *"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          data-testid="group-form-email"
          className="h-11"
        />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Input
          required
          placeholder="Group / organisation *"
          value={formData.organisation}
          onChange={(e) => setFormData({ ...formData, organisation: e.target.value })}
          data-testid="group-form-organisation"
          className="h-11"
        />
        <Input
          placeholder="Your role (e.g. Chair, Secretary)"
          value={formData.role}
          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
          data-testid="group-form-role"
          className="h-11"
        />
      </div>
      <select
        value={formData.group_type}
        onChange={(e) => setFormData({ ...formData, group_type: e.target.value })}
        data-testid="group-form-type"
        className="w-full h-11 rounded-md border border-input bg-white px-3 text-sm text-[#3B3535] focus:outline-none focus:ring-2 focus:ring-[#B5541B]/30"
      >
        <option value="">Group type…</option>
        {GROUP_TYPES.map((t) => (
          <option key={t} value={t}>{t}</option>
        ))}
      </select>
      <Textarea
        placeholder="What would make resident engagement easier for your group?"
        value={formData.engagement_answer}
        onChange={(e) => setFormData({ ...formData, engagement_answer: e.target.value })}
        data-testid="group-form-answer"
        rows={4}
      />
      <label className="flex items-start gap-3 cursor-pointer">
        <Checkbox
          checked={formData.early_testing}
          onCheckedChange={(v) => setFormData({ ...formData, early_testing: !!v })}
          data-testid="group-form-early-testing"
          className="mt-0.5"
        />
        <span className="text-sm text-[#3B3535]/80 leading-relaxed">
          I'm interested in early testing / pilot conversations
        </span>
      </label>
      <Button
        type="submit"
        disabled={isSubmitting}
        data-testid="group-form-submit"
        className="w-full bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold h-12 rounded-full shadow-md hover:shadow-lg transition-all duration-300"
      >
        {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <>
            Talk to us about your group
            <Send className="ml-2 w-4 h-4" />
          </>
        )}
      </Button>
      <p className="text-xs text-[#3B3535]/55 text-center leading-relaxed">
        Your details are used only to arrange this conversation and shape ResiAX: Community. See our{' '}
        <a href="/privacy" className="font-semibold text-[#B5541B]">Privacy Policy</a>.
      </p>
    </form>
  );
};

export default GroupInterestForm;
